import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import {
  ResponsiveContainer, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, Tooltip, CartesianGrid, Cell, LabelList,
} from "recharts";
import { Building2, TrendingUp, PiggyBank, Percent, DollarSign, Loader2 } from "lucide-react";

const FOREST = "#2A4B38";
const TERRACOTTA = "#D67049";
const LINE = "#E8E6E1";
const MUTE = "#73706A";
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const money2 = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Stat = ({ icon: Icon, label, value, tone = "forest" }) => (
  <div className="bg-white/70 border border-line rounded-xl p-3">
    <div className="flex items-center gap-1.5 text-mute"><Icon className={`h-3.5 w-3.5 text-${tone}`} /><span className="text-[11px] font-medium">{label}</span></div>
    <p className="text-lg font-heading font-semibold text-ink mt-1">{value}</p>
  </div>
);

export const PortfolioOverview = ({ year }) => {
  const [data, setData] = useState(null);
  const [prior, setPrior] = useState(null);
  const [compare, setCompare] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/landlord/portfolio?year=${year}`);
      setData(d);
    } catch (e) { /* pro-gated / empty */ }
    finally { setLoading(false); }
  }, [year]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPrior(null); setCompare(false); }, [year]);

  const toggleCompare = async () => {
    const next = !compare;
    setCompare(next);
    if (next && !prior) {
      try { const { data: p } = await api.get(`/landlord/portfolio?year=${year - 1}`); setPrior(p); }
      catch (e) { /* ignore */ }
    }
  };

  if (loading) return <div data-testid="portfolio-loading" className="text-sm text-mute py-6 text-center"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading portfolio…</div>;
  if (!data || data.property_count === 0) return <div className="text-sm text-mute py-6 text-center">Add properties to see a portfolio roll-up.</div>;

  const roiBars = data.properties.filter((p) => p.annual_roi != null).map((p) => ({ name: p.title, roi: p.annual_roi }));
  const cumulative = MONTHS.map((m, i) => ({ month: m, cumulative: (data.cumulative_net || [])[i] ?? null }));

  const priorMap = {};
  (prior?.properties || []).forEach((p) => { priorMap[p.id] = p; });
  const compareBars = data.properties.map((p) => ({
    name: p.title,
    current: p.annual_roi,
    prior: priorMap[p.id]?.annual_roi ?? null,
  })).filter((r) => r.current != null || r.prior != null);

  const delta = (cur, prv) => (cur == null || prv == null ? null : Math.round((cur - prv) * 10) / 10);
  const DeltaChip = ({ cur, prv, suffix = "%" }) => {
    const d = delta(cur, prv);
    if (d === null) return null;
    const up = d >= 0;
    return <span className={`ml-1 text-[10px] font-medium ${up ? "text-emerald-600" : "text-red-600"}`}>{up ? "▲" : "▼"} {Math.abs(d)}{suffix} vs {year - 1}</span>;
  };
  const MoneyDeltaChip = ({ cur, prv }) => {
    if (prv == null) return null;
    const d = Math.round(cur - prv);
    const up = d >= 0;
    return <span className={`ml-1 text-[10px] font-medium ${up ? "text-emerald-600" : "text-red-600"}`}>{up ? "▲" : "▼"} {money(Math.abs(d))} vs {year - 1}</span>;
  };

  return (
    <div data-testid="portfolio-overview" className="bg-bone/50 border border-line rounded-xl p-4 space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <Building2 className="h-4 w-4 text-forest" />
        <h3 className="text-sm font-medium text-ink">Portfolio overview · {year}</h3>
        <span className="text-xs text-mute">({data.property_count} properties)</span>
        <button data-testid="portfolio-compare-toggle" onClick={toggleCompare} className={`ml-auto inline-flex items-center gap-1.5 text-xs border rounded-lg px-2.5 py-1.5 ${compare ? "border-forest bg-forest/5 text-forest" : "border-line text-ink hover:bg-white"}`}>
          <TrendingUp className="h-3.5 w-3.5" /> {compare ? `Comparing to ${year - 1}` : `Compare to ${year - 1}`}
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2.5">
        <div className="bg-white/70 border border-line rounded-xl p-3"><div className="flex items-center gap-1.5 text-mute"><DollarSign className="h-3.5 w-3.5 text-forest" /><span className="text-[11px] font-medium">Total rent</span></div><p className="text-lg font-heading font-semibold text-ink mt-1">{money2(data.totals.rent_income)}</p>{compare && prior && <MoneyDeltaChip cur={data.totals.rent_income} prv={prior.totals.rent_income} />}</div>
        <div className="bg-white/70 border border-line rounded-xl p-3"><div className="flex items-center gap-1.5 text-mute"><PiggyBank className="h-3.5 w-3.5 text-terracotta" /><span className="text-[11px] font-medium">Total expenses</span></div><p className="text-lg font-heading font-semibold text-ink mt-1">{money2(data.totals.total_expenses)}</p>{compare && prior && <MoneyDeltaChip cur={data.totals.total_expenses} prv={prior.totals.total_expenses} />}</div>
        <div className="bg-white/70 border border-line rounded-xl p-3"><div className="flex items-center gap-1.5 text-mute"><TrendingUp className="h-3.5 w-3.5 text-forest" /><span className="text-[11px] font-medium">Total NOI</span></div><p className="text-lg font-heading font-semibold text-ink mt-1">{money2(data.totals.noi)}</p>{compare && prior && <MoneyDeltaChip cur={data.totals.noi} prv={prior.totals.noi} />}</div>
        <div className="bg-white/70 border border-line rounded-xl p-3"><div className="flex items-center gap-1.5 text-mute"><PiggyBank className={`h-3.5 w-3.5 ${data.totals.cash_flow < 0 ? "text-terracotta" : "text-forest"}`} /><span className="text-[11px] font-medium">Total cash flow</span></div><p className="text-lg font-heading font-semibold text-ink mt-1">{money2(data.totals.cash_flow)}</p>{compare && prior && <MoneyDeltaChip cur={data.totals.cash_flow} prv={prior.totals.cash_flow} />}</div>
        <div className="bg-white/70 border border-line rounded-xl p-3"><div className="flex items-center gap-1.5 text-mute"><Percent className="h-3.5 w-3.5 text-forest" /><span className="text-[11px] font-medium">Portfolio cap</span></div><p className="text-lg font-heading font-semibold text-ink mt-1">{data.portfolio_cap_rate == null ? "—" : `${data.portfolio_cap_rate}%`}</p>{compare && prior && <DeltaChip cur={data.portfolio_cap_rate} prv={prior.portfolio_cap_rate} />}</div>
        <div className="bg-white/70 border border-line rounded-xl p-3" data-testid="portfolio-roi-stat"><div className="flex items-center gap-1.5 text-mute"><Percent className="h-3.5 w-3.5 text-forest" /><span className="text-[11px] font-medium">Portfolio ROI</span></div><p className="text-lg font-heading font-semibold text-ink mt-1">{data.portfolio_roi == null ? "—" : `${data.portfolio_roi}%`}</p>{compare && prior && <DeltaChip cur={data.portfolio_roi} prv={prior.portfolio_roi} />}</div>
      </div>

      {compare && (
        <div data-testid="portfolio-compare-chart" className="bg-white border border-line rounded-xl p-4">
          <p className="text-sm font-medium text-ink">ROI by property · {year} vs {year - 1}</p>
          <p className="text-xs text-mute mt-0.5 mb-2">See which homes improved year over year.</p>
          {compareBars.length === 0 ? (
            <div className="h-[220px] flex items-center justify-center text-sm text-mute text-center px-4">Add a purchase price to compare ROI across years.</div>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(220, compareBars.length * 52)}>
              <BarChart data={compareBars} layout="vertical" margin={{ top: 4, right: 44, left: 8, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={LINE} horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: MUTE }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}%`} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: MUTE }} axisLine={false} tickLine={false} width={110} />
                <Tooltip formatter={(v, n) => [v == null ? "—" : `${Number(v).toFixed(1)}%`, n === "current" ? String(year) : String(year - 1)]} />
                <Bar dataKey="prior" name="prior" fill="#B8C4BC" radius={[0, 3, 3, 0]} maxBarSize={14} />
                <Bar dataKey="current" name="current" fill={FOREST} radius={[0, 3, 3, 0]} maxBarSize={14} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-3">
        <div data-testid="portfolio-roi-chart" className="bg-white border border-line rounded-xl p-4">
          <p className="text-sm font-medium text-ink">Annual ROI by property</p>
          <p className="text-xs text-mute mt-0.5 mb-2">Which homes are working hardest for you.</p>
          {roiBars.length === 0 ? (
            <div className="h-[220px] flex items-center justify-center text-sm text-mute text-center px-4">Add a purchase price on your properties to compare ROI.</div>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(220, roiBars.length * 44)}>
              <BarChart data={roiBars} layout="vertical" margin={{ top: 4, right: 40, left: 8, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={LINE} horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: MUTE }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}%`} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: MUTE }} axisLine={false} tickLine={false} width={110} />
                <Tooltip formatter={(v) => [`${Number(v).toFixed(1)}%`, "ROI"]} />
                <Bar dataKey="roi" radius={[0, 4, 4, 0]} maxBarSize={26}>
                  {roiBars.map((e, i) => <Cell key={i} fill={e.roi < 0 ? TERRACOTTA : FOREST} />)}
                  <LabelList dataKey="roi" position="right" formatter={(v) => `${Number(v).toFixed(1)}%`} style={{ fontSize: 11, fill: MUTE }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div data-testid="portfolio-cumulative-chart" className="bg-white border border-line rounded-xl p-4">
          <p className="text-sm font-medium text-ink">Combined cumulative net cash flow</p>
          <p className="text-xs text-mute mt-0.5 mb-2">Your whole portfolio's bottom line across {year}.</p>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={cumulative} margin={{ top: 6, right: 8, left: -12, bottom: 0 }}>
              <defs>
                <linearGradient id="pfNet" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={FOREST} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={FOREST} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={LINE} vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: MUTE }} axisLine={{ stroke: LINE }} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: MUTE }} axisLine={false} tickLine={false} tickFormatter={money} width={56} />
              <Tooltip formatter={(v) => [money2(v), "Cumulative net"]} />
              <Area type="monotone" dataKey="cumulative" stroke={FOREST} strokeWidth={2} fill="url(#pfNet)" connectNulls={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div data-testid="portfolio-table" className="bg-white border border-line rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-mute border-b border-line">
                <th className="p-3 font-medium">Property</th>
                <th className="p-3 font-medium text-right">Rent</th>
                <th className="p-3 font-medium text-right">Expenses</th>
                <th className="p-3 font-medium text-right">NOI</th>
                <th className="p-3 font-medium text-right">Cash flow</th>
                <th className="p-3 font-medium text-right">Cap rate</th>
                <th className="p-3 font-medium text-right">ROI</th>
              </tr>
            </thead>
            <tbody>
              {data.properties.map((p) => (
                <tr key={p.id} data-testid={`portfolio-row-${p.id}`} className="border-b border-line last:border-0">
                  <td className="p-3 text-ink font-medium">{p.title}</td>
                  <td className="p-3 text-right tabular-nums text-ink">{money2(p.rent_income)}</td>
                  <td className="p-3 text-right tabular-nums text-ink">{money2(p.total_expenses)}</td>
                  <td className="p-3 text-right tabular-nums text-ink">{money2(p.noi)}</td>
                  <td className={`p-3 text-right tabular-nums font-medium ${p.cash_flow < 0 ? "text-red-600" : "text-forest"}`}>{money2(p.cash_flow)}</td>
                  <td className="p-3 text-right tabular-nums text-ink">{p.cap_rate == null ? "—" : `${p.cap_rate}%`}</td>
                  <td className={`p-3 text-right tabular-nums font-medium ${p.annual_roi == null ? "text-mute" : p.annual_roi < 0 ? "text-red-600" : "text-forest"}`}>{p.annual_roi == null ? "—" : `${p.annual_roi}%`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
